import React, { useCallback, useEffect, useState } from "react";
import { Card, CardBody, Col, Label, Row } from "reactstrap";
import { FormLead } from "../../../../cores/facebookForm/interfaces/api/get-facebook-form";
import { useFacebookFormCore } from "../../../../cores/facebookForm";
import { View } from "../../../../cores/facebookForm/constants/view";
import cn from "classnames";

interface Props {
  facebookFormLead: FormLead;
  questions: string[];
  mode?: string;
  isCreatingContact?: boolean;
  isContactActionDisabled?: boolean;
  onOpenContact?: () => void | Promise<void>;
  onLoadingChange?: (loading: boolean) => void;
}

export default function LeadCard(props: Props) {
  const {
    facebookFormLead,
    questions,
    mode,
    isCreatingContact = false,
    isContactActionDisabled = false,
    onOpenContact,
    onLoadingChange,
  } = props;
  const [isLoading, setIsLoading] = useState(false);
  const { view } = useFacebookFormCore();
  const effectiveView = mode || view;
  const showLoading = isLoading || isCreatingContact;

  useEffect(() => {
    onLoadingChange?.(showLoading);
  }, [onLoadingChange, showLoading]);

  const handleOpenContact = useCallback(async () => {
    if (!onOpenContact || isLoading || isContactActionDisabled) return;

    setIsLoading(true);
    try {
      await onOpenContact();
    } finally {
      setIsLoading(false);
    }
  }, [isContactActionDisabled, isLoading, onOpenContact]);

  return (
    <Card key={facebookFormLead.id} className="facebook-lead-card border-0">
      <CardBody className="facebook-lead-card__body">
        <Row>
          <Col
            md={effectiveView === View.GRID ? "12" : "8"}
            xs="12"
            className={cn("d-flex flex-column", {
              "text-center":
                effectiveView === View.GRID || effectiveView === "cards",
            })}
          >
            {facebookFormLead.data.map((data, index) => (
              <Label key={`${facebookFormLead.id}-${index}`} className="facebook-lead-card__field">
                {effectiveView === View.LIST && (
                  <span className="facebook-lead-card__question">{questions[index]}: </span>
                )}
                <span className="facebook-lead-card__value">{data}</span>
              </Label>
            ))}
          </Col>
          <Col
            md={effectiveView === View.GRID ? "12" : "4"}
            xs="12"
            className={cn({
              "text-center":
                effectiveView === View.GRID || effectiveView === "cards",
              "text-right": effectiveView === View.LIST,
            })}
          >
            <span className="facebook-lead-card__time">{facebookFormLead.createdTime}</span>
          </Col>
        </Row>
        {onOpenContact ? (
          <div
            className={cn("mt-2 d-flex facebook-lead-card__action-row", {
              "justify-content-center":
                effectiveView === View.GRID || effectiveView === "cards",
              "justify-content-end": effectiveView === View.LIST,
            })}
          >
            <button
              type="button"
              className={cn("facebook-lead-card__action", {
                "facebook-lead-card__action--loading": showLoading,
              })}
              disabled={isContactActionDisabled || showLoading}
              onClick={handleOpenContact}
              aria-busy={showLoading}
            >
              {showLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin facebook-lead-card__action-spinner" />
                  <span>Procesando...</span>
                </>
              ) : (
                <span>Abrir / crear contacto</span>
              )}
            </button>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
